import type { UsageCorrection, SharedUsageItem } from '@/schemas/general/types'
import type { SetupSummaryListItems, SummaryListItemType } from '@/types'
import { formatCHF, formatNumberWithThousandSeparators } from '@/helpers/format'
import { getUsageDisplayTitle } from '@/helpers/usage-display'

type TranslationFn = (key: string) => string
type WidgetTextFn = (...path: string[]) => string

export interface SetupSummaryInput {
  quantityArea: number
  sharedUsages: SharedUsageItem[]
  isLandscapeArchitecture?: boolean
  isCivilEngineer?: boolean
  isSIA108?: boolean
  generalData: Record<string, unknown> | null
  corrections: UsageCorrection[]
  minutesPerSquareMeterForUsages: number[]
  complexityForUsages: number[]
  estimatedBuildingCost: number
  risk?: string
  riskRemarks?: string
  buildingCost?: number
  constructionCost?: number
  resolveBuildingTypeName?: (buildingTypeId?: string | null) => string | null
}

type RequirementsLevel = 'LOW' | 'STANDARD' | 'HIGH' | 'VERY_HIGH'
type UsageWithRequirementsLevel = SharedUsageItem & {
  requirementsLevel?: RequirementsLevel | string | null
  buildingTypeName?: string | null
  builtOverAreaRatio?: number | null
  belowTerrainAreaRatio?: number | null
  repetitionFactor?: number | null
  technologyFactor?: number | null
}

const LEGACY_TASK_FIELDS = [
  'buildingPlan',
  'rawConstruction',
  'facadeEnvelope',
  'interiors',
  'electricInstallation',
  'heatingVentilation',
  'sanitary',
] as const
const REQUIREMENTS_LEVELS: RequirementsLevel[] = ['LOW', 'STANDARD', 'HIGH', 'VERY_HIGH']
const DEFAULT_REQUIREMENTS_LEVEL: RequirementsLevel = 'STANDARD'
const fallbackRequirementLevelLabels: Record<RequirementsLevel, string> = {
  LOW: 'Low',
  STANDARD: 'Standard',
  HIGH: 'High',
  VERY_HIGH: 'Very high',
}
const DEFAULT_REPETITION_FACTOR = 1.0
const DEFAULT_TECHNOLOGY_FACTOR = 1.0

function isLegacyTaskModel(generalData: Record<string, unknown> | null): boolean {
  if (!generalData) return false
  return LEGACY_TASK_FIELDS.some((field) => field in generalData)
}

function hasPerUsageTaskFields(sharedUsages: SharedUsageItem[]): boolean {
  return sharedUsages.some((usage) => 'buildingPlan' in usage)
}

function normalizeRequirementsLevel(level: unknown): RequirementsLevel {
  if (REQUIREMENTS_LEVELS.includes(level as RequirementsLevel)) {
    return level as RequirementsLevel
  }
  return DEFAULT_REQUIREMENTS_LEVEL
}

function normalizeRepetitionFactor(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : DEFAULT_REPETITION_FACTOR
}

function normalizeTechnologyFactor(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : DEFAULT_TECHNOLOGY_FACTOR
}

/**
 * Computes the effective effort (hours/m2) for a single usage row,
 * replicating the getMinutesPerSquareMeterForUsageAtIndex getter logic.
 */
function getEffortForUsage(
  index: number,
  corrections: UsageCorrection[],
  minutesPerSquareMeter: number[],
  complexityForUsages: number[]
): number {
  const correction = corrections.find((c) => c.usageIndex === index)

  if (correction?.manualComplexity && correction.complexityFactorOverride == null) {
    return correction.manualComplexity
  }

  const factor = correction?.complexityFactorOverride ?? (complexityForUsages[index] || 1)
  if (correction?.manualArea) {
    return correction.manualArea * factor
  }

  return minutesPerSquareMeter[index] * factor || 0
}

export function renderSetupSummary(
  input: SetupSummaryInput,
  t: TranslationFn,
  wt: WidgetTextFn,
  wtSetup: WidgetTextFn,
  wtSummary: WidgetTextFn
): SetupSummaryListItems {
  const {
    quantityArea,
    sharedUsages,
    isLandscapeArchitecture = false,
    isCivilEngineer = false,
    isSIA108 = false,
    generalData,
    corrections,
    minutesPerSquareMeterForUsages,
    complexityForUsages,
    estimatedBuildingCost,
    risk,
    riskRemarks,
    buildingCost,
    constructionCost,
    resolveBuildingTypeName,
  } = input
  const shouldShowPerUsageTaskSummary = hasPerUsageTaskFields(sharedUsages) || !isLegacyTaskModel(generalData)

  const usageSections: { title: string; items: SummaryListItemType[] }[] = []
  let costList: SummaryListItemType[] = []

  const riskList: SummaryListItemType[] = [
    {
      title: wtSummary('pdf', 'project', 'setup', 'risk', 'title'),
      value: risk ? wtSetup('risk', 'option', risk) : '',
      additionalClass: 'title-item',
    },
    {
      title: wtSummary('pdf', 'project', 'setup', 'risk', 'description'),
      value: (riskRemarks as string) ?? '',
      additionalClass: 'text-block',
    },
  ]

  if (quantityArea && sharedUsages.length) {
    sharedUsages.forEach((usage, i) => {
      const area = (usage.ratio * quantityArea) / 100
      const usageTitle = getUsageDisplayTitle(usage as UsageWithRequirementsLevel, wt, {
        allUsages: sharedUsages as UsageWithRequirementsLevel[],
        index: i,
        resolveBuildingTypeName,
      })

      const correction = corrections.find((c) => c.usageIndex === i)
      const effort = Math.round(getEffortForUsage(i, corrections, minutesPerSquareMeterForUsages, complexityForUsages))

      const items: SummaryListItemType[] = []

      if (shouldShowPerUsageTaskSummary) {
        const planLabel = wt('requirementsAndTasks', 'task', 'planType', 'option', usage.buildingPlan)
        const requirementLevelTitle = wt('requirementsAndTasks', 'requirementsLevel', 'title') || 'Requirement level'
        const level = normalizeRequirementsLevel((usage as UsageWithRequirementsLevel).requirementsLevel)
        const requirementLevelValue =
          wt('requirementsAndTasks', 'requirementsLevel', 'option', level) || fallbackRequirementLevelLabels[level]

        items.push({
          title: wtSummary('pdf', 'project', 'setup', 'usage', 'buildingPlan'),
          value: planLabel,
          additionalClass: 'dark-title',
        })
        items.push({
          title: requirementLevelTitle,
          value: requirementLevelValue,
          additionalClass: 'dark-title',
        })
        if (isCivilEngineer) {
          items.push({
            title: wt('requirementsAndTasks', 'task', 'repetitionFactor', 'valueLabel') || 'Repetition factor',
            value: normalizeRepetitionFactor((usage as UsageWithRequirementsLevel).repetitionFactor).toFixed(2),
            additionalClass: 'dark-title',
          })
        }
        if (isSIA108) {
          items.push({
            title: wt('requirementsAndTasks', 'task', 'technologyFactor', 'valueLabel') || 'Technology degree factor',
            value: normalizeTechnologyFactor((usage as UsageWithRequirementsLevel).technologyFactor).toFixed(2),
            additionalClass: 'dark-title',
          })
        }
      }

      // Usage data
      const areaTitle = isLandscapeArchitecture
        ? wtSummary('summary', 'overall', 'landscapeQuantityArea')
        : wtSummary('pdf', 'project', 'setup', 'usage', 'area')
      items.push({
        title: areaTitle,
        value: formatNumberWithThousandSeparators(area) + ' ' + t('general.sq_m'),
        additionalClass: 'dark-title',
      })
      if (isLandscapeArchitecture) {
        items.push({
          title: wt('areaDistribution', 'landscape', 'builtOverArea', 'title'),
          value:
            formatNumberWithThousandSeparators((usage as UsageWithRequirementsLevel).builtOverAreaRatio ?? 0) + ' %',
          additionalClass: 'dark-title',
        })
      }
      if (isCivilEngineer) {
        items.push({
          title: wt('areaDistribution', 'civilEngineer', 'terrain', 'option', 'BELOW_TERRAIN'),
          value:
            formatNumberWithThousandSeparators((usage as UsageWithRequirementsLevel).belowTerrainAreaRatio ?? 0) + ' %',
          additionalClass: 'dark-title',
        })
      }
      items.push({
        title: wtSummary('pdf', 'project', 'setup', 'usage', 'effort'),
        value: effort + ' ' + t('general.min_per_sq_m'),
        additionalClass: 'dark-title',
      })

      // Slider values
      const sliderCreative = correction?.sliderCreative ?? 1.0
      const sliderTechnical = correction?.sliderTechnical ?? 1.0
      const sliderSpatial = correction?.sliderSpatial ?? 1.0
      const sliderOrganizational = correction?.sliderOrganizational ?? 1.0

      const dimensions = [
        { key: 'creative', slider: sliderCreative, justification: correction?.justificationCreative },
        { key: 'technical', slider: sliderTechnical, justification: correction?.justificationTechnical },
        { key: 'spatial', slider: sliderSpatial, justification: correction?.justificationSpatial },
        { key: 'organizational', slider: sliderOrganizational, justification: correction?.justificationOrganizational },
      ]

      for (const dim of dimensions) {
        items.push({
          title: wtSetup('complexity', 'dimensions', dim.key, 'title'),
          value: dim.slider.toFixed(1),
          additionalClass: 'muted-value',
        })

        if (dim.justification && dim.justification.length > 0) {
          items.push({
            title: dim.justification,
            value: '',
            additionalClass: 'justification-text',
          })
        }
      }

      // Overall complexity factor
      const overallComplexity =
        Math.round(
          ((sliderCreative + sliderTechnical + sliderSpatial + sliderOrganizational) / 4 + Number.EPSILON) * 20
        ) / 20

      items.push({
        title: wtSummary('pdf', 'project', 'setup', 'complexity', 'overall'),
        value: (correction?.complexityFactorOverride ?? overallComplexity).toFixed(2),
        additionalClass: 'highlight-item',
      })

      usageSections.push({ title: usageTitle, items })
    })
  }

  if (buildingCost && constructionCost) {
    const estimatedCost = estimatedBuildingCost || 0
    const diff_buildingCost = (estimatedCost - buildingCost) / (buildingCost / 100)
    const diff_constructionCost = (estimatedCost * 100) / constructionCost
    costList = [
      {
        title: wtSummary('pdf', 'project', 'setup', 'cost', 'title'),
        value: formatCHF(estimatedCost) + ' ' + t('general.currency'),
        additionalClass: 'title-item',
      },
      {
        title: wtSummary('pdf', 'project', 'setup', 'cost', 'budgetBuildingCost'),
        value: formatCHF(buildingCost) + ' ' + t('general.currency'),
      },
      {
        title: wtSummary('pdf', 'project', 'setup', 'cost', 'diffBuildingCost'),
        value: formatNumberWithThousandSeparators(diff_buildingCost) + ' %',
      },
      {
        title: wtSummary('pdf', 'project', 'setup', 'cost', 'budgetConstructionCost'),
        value: formatCHF(constructionCost) + ' ' + t('general.currency'),
      },
      {
        title: wtSummary('pdf', 'project', 'setup', 'cost', 'diffConstructionCost'),
        value: formatNumberWithThousandSeparators(diff_constructionCost) + ' %',
      },
    ]
  }

  return { usageSections, riskList, costList }
}
